import t from '@jetshop/intl';
import Accordion from '@jetshop/ui/Accordion/Accordion';
import ProductSpecifications from '@jetshop/ui/ProductSpecifications/ProductSpecifications';
import React, { Fragment, useEffect } from 'react';
import { styled } from 'linaria/react';
import {
  ProductSection as UIProductSection,
  SectionHeading,
} from './ProductInfo';
import { ReactComponent as Up } from '../../svg/CarretUp.svg';
import { ReactComponent as Down } from '../../svg/CarretDown.svg';

import { DownloadableFiles } from './DownloadableFiles';
import { theme } from '../Theme';
import { useQuery } from '@apollo/react-hooks';
import ExtraProductContentQuery from './ExtraProductContentQuery.gql';
import { ContentRenderer } from '@jetshop/ui/ContentRenderer';
import { contentEditorComponents } from '../ContentEditor/ContentEditorComponents';
import { LipscoreProductReview } from './Lipscore/LipscoreProductReview';
import { ContactStore } from './ContactStore';
import { AccordionContent } from './AccordionContent.js';
import { useLipscore } from '../../hooks/useLipscore';

const Description = styled('article')`
  line-height: 25px;
  font-size: 14px;
  font-weight: 400;
`;

const ProductSection = styled(UIProductSection)`
  width: 100%;
  padding: 5px 20px;
  margin: 0;
  position: relative;
  border-top: 5px solid rgba(246, 242, 234, 0.6);
  ${SectionHeading} {
    display: flex;
    justify-items: space-between;
    align-items: center;
    margin-bottom: 0;
    + * > :first-child {
      margin-top: 1rem;
    }
  }

  ${theme.below.lg} {
    padding: 5px 14px;
  }

  .scroll-to-placeholder {
    position: absolute;
    top: -100px;
    left: 0;
    right: 0;
    height: 1px;
    width: 100%;
    pointer-events: none;

    ${theme.below.lg} {
      top: -80px;
    }
  }

  .lipscore-rating-small {
    display: flex;
    align-items: center;

    .lipscore-rating-star {
      fill: #feb846;
      height: 14px;
      width: 14px;
    }

    .lipscore-compact-rating-value,
    .lipscore-compact-rating-votes {
      font-family: ${theme.fonts.primary};
      font-size: 12px;
      line-height: 14px;
      margin-left: 3px;
    }
  }

  .rating-preview {
    display: flex;
    align-items: center;
    /* gap: 2px; */

    .rating-star {
      width: 14px;
      height: 14px;
    }

    .rating-value {
      margin-left: 5px;
      font-family: ${theme.fonts.primary};
      font-size: 12px;
      line-height: 14px;
      color: #333;
    }
  }
`;

const IndicatorIcon = styled('span')`
  margin-left: auto;
  font-size: 1.25rem;
`;

const ExtraContentAccordionWrapper = styled('div')`
  .content-row-wrapper {
    padding: 0;
  }
`;

//extra product content
const ExtraProductContent = ({ contentid }) => {
  const { data, loading, error } = useQuery(ExtraProductContentQuery, {
    variables: {
      id: contentid,
    },
  });

  if (loading || error) return null;

  return (
    <ContentRenderer
      items={data?.category?.data?.items}
      components={contentEditorComponents}
    />
  );
};

const AccordionIndicator = ({ isOpen }) => (
  <IndicatorIcon>{isOpen ? <Up /> : <Down />}</IndicatorIcon>
);

const getStarFill = (starIndex, rating) => {
  const ratingNum = parseFloat(rating);
  if (starIndex < Math.floor(ratingNum)) {
    return '#feb846'; // Full star
  } else if (starIndex === Math.floor(ratingNum) && ratingNum % 1 !== 0) {
    // Return a partially filled star using a linear gradient
    const percentage = Math.round((ratingNum % 1) * 100);
    return `url(#partialFill-${percentage})`;
  }
  return '#D9D9D9'; // Empty star
};

const ProductInfoAccordion = ({
  product,
  storeName,
  contactOpen,
  setContactOpen,
  rating,
  reviewCount,
}) => {
  useLipscore();

  const excludes = [
    'Mastarpris',
    'extracontent',
    'configurator',
    'store',
    'files',
    'Upsell',
    'newConfigurator',
    'paket',
    'product-video-url',
    'product-video-aspect-ratio',
    'link-studio-3d',
  ];

  const productCustomFieldsAdjusted = product?.customFields
    ?.map((customfield) => {
      if (customfield.key == 'BenFotUnderrede') {
        return { ...customfield, title: 'Ben / Fot / Underrede' };
      }
      return { ...customfield, title: customfield.title.replace(/_/g, ' ') };
    })
    .filter((field) => !excludes.includes(field.key));
  const files = product?.customFields?.find(
    ({ key }) => key === 'files'
  )?.listValues;

  const extraProductContentID = product?.customFields?.find(
    ({ key }) => key === 'extracontent'
  )?.listValues;

  const mastarPris = product?.customFields?.find(
    ({ key }) => key === 'Mastarpris'
  )?.boolValue;

  useEffect(() => {
    if (contactOpen) {
      return null;
    }
  }, [contactOpen]);

  return (
    <>
      <Accordion single initialOpenIndexes={[0]}>
        {({ openIndexes, handleClick }) => (
          <Fragment>
            {contactOpen &&
              openIndexes[0] !== 6 &&
              (handleClick(6), setContactOpen(false))}
            <ProductSection>
              <div id="description" className="scroll-to-placeholder" />
              <SectionHeading onClick={() => handleClick(0)}>
                <span>{t('Product description')}</span>
                <AccordionIndicator isOpen={openIndexes.includes(0)} />
              </SectionHeading>
              <AccordionContent isOpen={openIndexes.includes(0)}>
                <Description
                  dangerouslySetInnerHTML={{
                    __html: product.description,
                  }}
                />
              </AccordionContent>
            </ProductSection>
            {productCustomFieldsAdjusted.length > 0 && (
              <ProductSection>
                <SectionHeading onClick={() => handleClick(1)}>
                  <span>{t('Specifications')}</span>
                  <AccordionIndicator isOpen={openIndexes.includes(1)} />
                </SectionHeading>
                <AccordionContent isOpen={openIndexes.includes(1)}>
                  <ProductSpecifications fields={productCustomFieldsAdjusted} />
                </AccordionContent>
              </ProductSection>
            )}
            <ProductSection>
              <div id="reviews" className="scroll-to-placeholder" />
              <SectionHeading data-section="2" onClick={() => handleClick(2)}>
                <span
                  style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                  {t('Reviews')}
                  {rating && (
                    <div className="rating-preview">
                      <svg width="0" height="0">
                        <defs>
                          <linearGradient
                            id={`partialFill-${Math.round(
                              (parseFloat(rating) % 1) * 100
                            )}`}
                          >
                            <stop
                              offset={`${Math.round(
                                (parseFloat(rating) % 1) * 100
                              )}%`}
                              stopColor="#feb846"
                            />
                            <stop
                              offset={`${Math.round(
                                (parseFloat(rating) % 1) * 100
                              )}%`}
                              stopColor="#D9D9D9"
                            />
                          </linearGradient>
                        </defs>
                      </svg>

                      {[...Array(5)].map((_, i) => (
                        <svg
                          key={i}
                          className="rating-star"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                        >
                          <path
                            d="M10 1L13 7L19 8L14.5 13L15.5 19L10 16L4.5 19L5.5 13L1 8L7 7L10 1Z"
                            fill={getStarFill(i, rating)}
                          />
                        </svg>
                      ))}
                      <span className="rating-value">
                        {rating}
                        {reviewCount && ` (${reviewCount})`}
                      </span>
                    </div>
                  )}
                </span>
                <AccordionIndicator isOpen={openIndexes.includes(2)} />
              </SectionHeading>
              <AccordionContent isOpen={openIndexes.includes(2)}>
                <LipscoreProductReview product={product} />
              </AccordionContent>
            </ProductSection>
            <ProductSection>
              <SectionHeading onClick={() => handleClick(3)}>
                <span>{t('Care advice')}</span>
                <AccordionIndicator isOpen={openIndexes.includes(3)} />
              </SectionHeading>
              <AccordionContent isOpen={openIndexes.includes(3)}>
                <ExtraContentAccordionWrapper>
                  {extraProductContentID &&
                    extraProductContentID.map((contentId) => {
                      return parseInt(contentId) === 332 ? null : (
                        <ExtraProductContent
                          key={contentId}
                          contentid={contentId}
                        />
                      );
                    })}
                </ExtraContentAccordionWrapper>
              </AccordionContent>
            </ProductSection>
            {files?.length > 0 && (
              <ProductSection className="files hideInPrint">
                <SectionHeading onClick={() => handleClick(4)}>
                  <span>{t('Download')}</span>
                  <AccordionIndicator isOpen={openIndexes.includes(4)} />
                </SectionHeading>
                <AccordionContent isOpen={openIndexes.includes(4)}>
                  <DownloadableFiles files={files} />
                </AccordionContent>
              </ProductSection>
            )}
            {mastarPris && (
              <ProductSection>
                <div id="mastarpris" className="scroll-to-placeholder" />
                <SectionHeading
                  data-section="mastarpris"
                  onClick={() => handleClick(5)}
                >
                  <span>{t('Masterprice - Extra benefical')}</span>
                  <AccordionIndicator isOpen={openIndexes.includes(5)} />
                </SectionHeading>
                <AccordionContent isOpen={openIndexes.includes(5)}>
                  <ExtraContentAccordionWrapper>
                    <ExtraProductContent contentid={332} />
                  </ExtraContentAccordionWrapper>
                </AccordionContent>
              </ProductSection>
            )}
            <ProductSection id="contact">
              <SectionHeading onClick={() => handleClick(6)}>
                <span>{t('Contact Store')}</span>
                <AccordionIndicator isOpen={openIndexes.includes(6)} />
              </SectionHeading>
              <AccordionContent isOpen={openIndexes.includes(6)}>
                <ContactStore product={product} />
              </AccordionContent>
            </ProductSection>
          </Fragment>
        )}
      </Accordion>
    </>
  );
};

export default ProductInfoAccordion;
